// SAF-18432-Safety - User Guide Integration
// This component displays a list of user guides in a grid, allowing users to view or download each guide.

import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Page from 'spark-ui/ui-kitchen/ingredients/base/Page';
import { GetApp, Visibility } from '@material-ui/icons';
import { GridProvider, WithResults, WithoutResults, GridBody } from 'spark-grid';
import { PaginationFooter } from 'spark-grid';
import TableWrapper from 'spark-ui/Grid/TableWrapper';
import GridHeaderRow from 'spark-ui/Grid/header/GridHeaderRow';
import HeaderCell from 'spark-ui/Grid/header/headerCells/HeaderCell';
import TableRow from 'spark-ui/Grid/tableBody/TableRow';
import { TableCell } from 'material-ui/Table';
import IconButton from 'material-ui/IconButton';
import { getUserGuides, viewUserGuide, downloadUserGuide } from 'spark-api/utilityService';
import { setUserGuides, getUserGuideById } from 'spark-state/common/user/userReducer';
import InlineDocViewer from 'spark-ui/InlineDocViewer/InlineViewer';

const ActionsCell = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const GRID_NAME = 'userGuidesGrid';

// Row component - used to fetch individual guide from Redux state by rowId
const GridRowComponent = connect(
    (state, { rowId }) => ({
        guide: getUserGuideById(state, rowId)
    })
)(({ guide, onView, onDownload }) => {
    if (!guide) return null;
    return (
        <TableRow>
            <TableCell>{guide.displayName || 'N/A'}</TableCell>
            <TableCell style={{ width: '120px' }}>
                <ActionsCell>
                    <IconButton
                        onClick={() => onView(guide)}
                        title="View Document"
                    >
                        <Visibility />
                    </IconButton>
                    <IconButton
                        onClick={() => onDownload(guide)}
                        title="Download Document"
                    >
                        <GetApp />
                    </IconButton>
                </ActionsCell>
            </TableCell>
        </TableRow>
    );
});

const UserGuides = ({ dispatch }) => {
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [fileDetails, setFileDetails] = useState(null);
    const [viewingGuide, setViewingGuide] = useState(null);

    const handleView = (guide) => {
        // Open the viewer immediately — InlineViewer shows "Loading document..." until fileDetails is set
        setViewingGuide(guide);
        setFileDetails(null);
        setIsViewerOpen(true);

        dispatch(viewUserGuide(guide.id))
            .then((response) => {
                if (response && response.data) {
                    const bytes = new Uint8Array(response.data);
                    let binary = '';
                    for (let i = 0; i < bytes.byteLength; i++) {
                        binary += String.fromCharCode(bytes[i]);
                    }
                    const base64 = window.btoa(binary);
                    setFileDetails({
                        processedContentType: 'application/pdf',
                        byteArray: base64
                    });
                } else {
                    setFileDetails({ success: false });
                }
            })
            .catch(() => {
                setFileDetails({ success: false });
            });
    };

    const handleDownload = (guide) => {
        dispatch(downloadUserGuide(guide.id, `${guide.displayName || 'user-guide'}.pdf`));
    };

    if (isViewerOpen) {
        return (
            <InlineDocViewer
                fileDetails={fileDetails}
                fileName={viewingGuide ? (viewingGuide.displayName || 'User Guide') : 'User Guide'}
                goBack={() => {
                    setIsViewerOpen(false);
                    setFileDetails(null);
                    setViewingGuide(null);
                }}
            />
        );
    }

    return (
        <Page title="User Guides">
            <GridProvider
                name={GRID_NAME}
                loadData={getUserGuides}
                onDataReceived={setUserGuides}
                primaryKey="id"
                retainOnUnmount
            >
                <WithResults>
                    <TableWrapper>
                        <GridHeaderRow>
                            <HeaderCell>Guide Name</HeaderCell>
                            <HeaderCell style={{ width: '120px' }}>Actions</HeaderCell>
                        </GridHeaderRow>
                        <GridBody
                            rowComponent={({ rowId }) => (
                                <GridRowComponent
                                    rowId={rowId}
                                    onView={handleView}
                                    onDownload={handleDownload}
                                />
                            )}
                        />
                        <PaginationFooter />
                    </TableWrapper>
                </WithResults>
                <WithoutResults>
                    <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                        No user guides available at this time.
                    </div>
                </WithoutResults>
            </GridProvider>
        </Page>
    );
};

export default connect()(UserGuides);

